import H from 'constants/httpMethod.const';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import UMSG_TYPE from 'constants/umsg/type.const';
import fromDtoToStylist from 'dto/stylist/fromDtoToStylist.dto';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromAnyDateToIso, fromAnyDateToTimestamp } from 'utils/converters/time/from.util';
import logger from 'utils/logger.util';
import actionEndpointBe from 'utils/mw/actionEndpointBe.util';
import isNilValue from 'utils/predicates/any/isNilValue.util';
import isNotEnumVal from 'utils/predicates/enum/isNotEnumVal.util';
import { isNotValidId } from 'utils/predicates/fields.util';
import { isTimeBefore } from 'utils/predicates/time/compare.util';

const L = logger('beSidebarAppointment'); // eslint-disable-line no-unused-vars

const umsgIgnoring404 = (options, context) => {
  // a replacement for UMSG_TYPE.global because after deleting the appointment, we get 404
  if (context?.is?.notFound) {
    return void L.info('umsgIgnoring404()', 'UMSG ignoring not found error for:', { options, context });
  } else {
    return 'There has been a error reading the appointment';
  }
};

const fromDtoToClientTag = (dto) => ({
  id: dto?.id,
  salonId: dto?.salon_id,
  name: dto?.name,
  emoticon: dto?.emoticon,
  description: dto?.description,
});

const fromDtoToExpandedAppointmentClient = (dto) => ({
  activeCard: dto?.active_card,
  birthday: dto?.birthday,
  clientTags: asArray(dto?.client_tags).map(fromDtoToClientTag),
  consultationForm: dto?.consultation_form,
  id: dto?.id ?? dto?.pk,
  notes: dto?.notes,
  notifications: dto?.notifications,
  phone: dto?.phone,
  pk: dto?.pk,
  preferredStylist: dto?.preferred_stylist,
  patchTestDate: dto?.patch_test_date,
  stoppedSms: dto?.stopped_sms, // does not exist in the response
  requestedOptOut: dto?.requested_opt_out,
  userName: dto?.user_name,
});

const fromDtoToExpandedAppointmentProcedure = (dto) => ({
  appointment: dto?.appointment,
  duration: dto?.duration,
  id: dto?.id,
  isCustomProcessing: dto?.is_custom_processing,
  isCustomDuration: dto?.is_custom_duration,
  isCustomPrice: dto?.is_custom_price,
  parentId: dto?.parent_id,
  price: dto?.price,
  processing: dto?.processing,
  startTime: dto?.start_time,
  endTime: dto?.end_time,
  service: {
    category: {
      color: dto?.service?.category?.color,
      id: dto?.service?.category?.id,
      name: dto?.service?.category?.name,
    },
    id: dto?.service?.id,
    name: dto?.service?.name,
  },
  stylist: fromDtoToStylist(dto?.stylist),
});

const fromDtoToExpandedAppointment = (dto) => {
  const results = dto?.results ?? dto; // NOTE: the write version doesn't use the `results` field
  return {
    client: fromDtoToExpandedAppointmentClient(results?.client),
    deposit: {
      amount: results?.deposit?.amount,
      source: results?.deposit?.source,
      status: results?.deposit?.status,
    },
    frequency: results?.frequency,
    frequencyType: results?.frequency_type,
    invitation: results?.invitation,
    notes: results?.notes,
    origin: results?.origin,
    patchTestDateTime: results?.patch_test_date_time,
    procedures: asArray(results?.procedures).map(fromDtoToExpandedAppointmentProcedure),
    status: results?.status,
    isRepeating: results?.is_repeating,
    until: results?.until,
  };
};

const fromDtoToSmsRecord = (dto) => ({
  id: dto?.id,
  number: fromAnyToString(dto?.number), // phone
  text: fromAnyToString(dto?.text),
  timestamp: dto?.timestamp,
  status: fromAnyToTrimmed(dto?.status),
  smsType: fromAnyToTrimmed(dto?.sms_type),
  twilioStatus: fromAnyToTrimmed(dto?.twilio_status),
});

const fromDtoToSortedSmsRecords = (dto) => {
  const smsRecords = asArray(dto?.results?.sms_records)
    .sort((a, b) => fromAnyDateToTimestamp(a.timestamp) - fromAnyDateToTimestamp(b.timestamp))
    .map(fromDtoToSmsRecord);

  // .reduce() here has no init, so don't use it on an empty array
  const isoLatestTimestamp = smsRecords.length ? fromAnyDateToIso(smsRecords.map((r) => r.timestamp).reduce((latest, current) => (isTimeBefore(latest, current) ? current : latest))) : '';

  return { smsRecords, isoLatestTimestamp };
};

// history
const fromDtoToExpandedAppointmentHistoryProcedure = (dto) => ({
  price: dto?.price,
  startTime: dto?.start_time,
  endTime: dto?.end_time,
  duration: dto?.duration,
  processing: dto?.processing,
  service: {
    id: dto?.service?.id,
    name: dto?.service?.name,
    category: {
      id: dto?.service?.category?.id,
      name: dto?.service?.category?.name,
      color: dto?.service?.category?.color,
    },
  },
  stylist: {
    id: dto?.stylist?.id,
    userName: dto?.stylist?.user_name,
    email: dto?.stylist?.email,
    roleName: dto?.stylist?.role_name,
    servesAsWaitlist: dto?.stylist?.serves_as_waitlist,
  },
});

const fromDtoToExpandedAppointmentHistoryChange = (dto) => ({
  name: dto?.name,
  dateTime: dto?.date_time,
  now: dto?.now,
  was: dto?.was,
});

const fromDtoToExpandedAppointmentHistoryChangesData = (dto) => ({
  action: dto?.action,
  historyDate: dto?.history_date,
  procedureId: dto?.procedure_id,
  serviceName: dto?.service_name,
  changedBy: {
    name: dto?.changed_by?.name,
    email: dto?.changed_by?.email,
  },
  changes: asArray(dto?.changes).map(fromDtoToExpandedAppointmentHistoryChange),
});

const fromDtoToExpandedAppointmentHistory = (dto) => ({
  creationData: {
    origin: dto?.results?.creation_data?.origin,
    dateCreated: dto?.results?.creation_data?.date_created,
    createdBy: dto?.results?.creation_data?.created_by,
    action: dto?.results?.creation_data?.action,
    procedures: asArray(dto?.results?.creation_data?.procedures).map(fromDtoToExpandedAppointmentHistoryProcedure),
  },
  changesData: asArray(dto?.results?.changes_data).map(fromDtoToExpandedAppointmentHistoryChangesData),
});

export const ibeSidebarAppointmentHistoryRead = actionEndpointBe('ibeSidebarAppointmentHistoryRead', H.get, 'v2/salon/:salonId/appointment/log/:appointmentId/', (options, extra) => ({
  ...options,
  inert: true,
  params: { appointmentId: fromAnyToTrimmed(extra?.appointmentId) || 0 },
  umsg: UMSG_TYPE.global,
  fromDto: fromDtoToExpandedAppointmentHistory,
}));

// SEE: https://github.com/getslick/api.core/blob/staging/backend/urls.py
//    re_path(r'^salon/(?P<salon_id>[0-9]+)/fetch/appointment/(?P<appointment_id>[0-9]+)/$',
//             never_cache(SalonGetAppoinment.as_view()),
//             name="get-appointment-detail"),

export const IBE_SIDEBAR_APPOINTMENT_READ_PATTERN = 'salon/:salonId/fetch/appointment/:appointmentId/';
export const ibeSidebarAppointmentRead = actionEndpointBe('ibeSidebarAppointmentRead', H.get, IBE_SIDEBAR_APPOINTMENT_READ_PATTERN, (options, extra) => ({
  ...options,
  inert: true,
  isMissingParams: isNotValidId(extra?.appointmentId),
  params: { appointmentId: fromAnyToTrimmed(extra?.appointmentId) || 0 },
  umsg: umsgIgnoring404,
  fromDto: fromDtoToExpandedAppointment,
}));

export const ibeSidebarAppointmentSmsRead = actionEndpointBe('ibeSidebarAppointmentSmsRead', H.get, 'salon/v2/:salonId/sms/appointment/:appointmentId/', (options, extra) => ({
  ...options,
  inert: true,
  isMissingParams: isNotValidId(extra?.appointmentId),
  params: { appointmentId: fromAnyToTrimmed(extra?.appointmentId) || 0 },
  umsg: UMSG_TYPE.global,
  fromDto: fromDtoToSortedSmsRecords,
}));

export const ibeSidebarAppointmentStatusWrite = actionEndpointBe('ibeSidebarAppointmentStatusWrite', H.patch, 'salon/:salonId/appointment/:appointmentId/', (options, extra) => {
  const appointmentId = fromAnyToTrimmed(extra?.appointmentId) || 0;
  const status = fromAnyToTrimmed(extra?.status);

  return {
    ...options,
    inert: true,
    isMissingParams: isNotValidId(appointmentId) || isNotEnumVal(AP_STATUS, status),
    params: { appointmentId },
    data: { status },
    umsg: umsgIgnoring404,
    fromDto: fromDtoToExpandedAppointment,
  };
});

export const ibeSidebarAppointmentBookingNoteWrite = actionEndpointBe('ibeSidebarAppointmentBookingNoteWrite', H.patch, 'salon/:salonId/appointment/:appointmentId/', (options, extra) => {
  const note = extra?.note;
  const appointmentId = fromAnyToTrimmed(extra?.appointmentId) || 0;

  return {
    ...options,
    inert: true,
    isMissingParams: isNotValidId(appointmentId) || isNilValue(note),
    params: { appointmentId },
    data: { notes: fromAnyToString(note) },
    umsg: UMSG_TYPE.global,
    fromDto: fromDtoToExpandedAppointment,
  };
});

export const ibeSidebarAppointmentClientNoteWrite = actionEndpointBe('ibeSidebarAppointmentClientNoteWrite', H.patch, 'salon/:salonId/client/:clientId/', (options, extra) => {
  const note = extra?.note;
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    inert: true,
    isMissingParams: isNotValidId(clientId) || isNilValue(note),
    params: { clientId },
    data: { notes: fromAnyToString(note) },
    umsg: UMSG_TYPE.global,
    fromDto: fromDtoToExpandedAppointment,
  };
});
